<script setup lang="ts">
import { ButtonComponent } from '@metapic-apps/shared'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import NotificationList from './NotificationList.vue'

import CheckIcon from '@/assets/icons/check-icon.svg?component'
import MoreIcon from '@/assets/icons/more.svg?component'
import { type NotificationType } from '@/lib/types'
import { useNotificationStore } from '@/stores/notifications.stores'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const activeTab = defineModel<'all' | 'unread'>('modelValue')
const notificationStore = useNotificationStore()
const router = useRouter()
const isServiceUnavailable = computed(() => notificationStore.notificationsUnavailable)

const emit = defineEmits(['close'])
const updateReadStatus = async (id: string, read: boolean) => notificationStore.updateReadStatus(id, read)
const handleMarkAllAsRead = async () => await notificationStore.markAllAsRead()

const markSameCreatorCampaignNotifications = (identifier: string) => {
  notificationStore.all.data.find((notification) => {
    if (!notification.read) {
      notification.data.events.forEach((event) => {
        if (event.payload?._identifier === identifier) {
          void notificationStore.updateReadStatus(notification.id, true)
        }
      })
    }
  })
}
const handleItemClick = (item: NotificationType) => {
  const payload = item.data.events[0]?.payload
  const isGroupedNotification = item.data.events.length > 1
  markSameCreatorCampaignNotifications(payload?._identifier)
  if (payload) {
    emit('close')
    return router.push(
      isGroupedNotification
        ? `/campaign-users/${payload.campaign.id}`
        : `/campaign-users/${payload.campaign.id}?participant=${payload.participant.id}`,
    )
  }
}
</script>
<template>
  <BoxComponent class="notification-content__wrapper">
    <BoxComponent :pt="8" :mb="16" class="notification-content__container">
      <BoxComponent :pl="16" :pr="8">
        <FlexComponent justify="space-between" align="center" :gap="16">
          <TypographyComponent variant="headline-small">Notifications</TypographyComponent>
          <el-dropdown trigger="click" placement="bottom-end">
            <ButtonComponent is-icon variant="text"><MoreIcon /></ButtonComponent>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleMarkAllAsRead">
                  <BoxComponent :pt="12" :pb="12">
                    <FlexComponent align="center" :gap="8">
                      <el-icon :size="16">
                        <CheckIcon />
                      </el-icon>
                      <TypographyComponent variant="body-large">Mark all as read</TypographyComponent>
                    </FlexComponent>
                  </BoxComponent>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent v-if="isServiceUnavailable" class="notification-content__unavailable">
        <ElAlert title="Notifications are currently unavailable." type="warning" :closable="false" show-icon />
      </BoxComponent>
      <template v-else>
        <BoxComponent :p="16" class="notification-content__buttons">
          <FlexComponent :gap="8">
            <ButtonComponent :variant="activeTab === 'all' ? 'primary' : 'secondary'" @click="activeTab = 'all'">All</ButtonComponent>
            <ButtonComponent :variant="activeTab === 'unread' ? 'primary' : 'secondary'" @click="activeTab = 'unread'">
              Unread {{ notificationStore.unread.total ? `(${notificationStore.unread.total})` : '' }}
            </ButtonComponent>
          </FlexComponent>
        </BoxComponent>
        <BoxComponent class="notification-content__content">
          <transition name="slide">
            <NotificationList
              v-if="activeTab === 'all'"
              :items="notificationStore.all.data"
              :status="notificationStore.all.status"
              :has-more="notificationStore.all.hasMore"
              @fetch="notificationStore.fetchAll"
              @toggle-status="updateReadStatus"
              @item-click="handleItemClick"
            />
            <NotificationList
              v-else
              :items="notificationStore.unread.data"
              :status="notificationStore.unread.status"
              :has-more="notificationStore.unread.hasMore"
              @fetch="notificationStore.fetchUnread"
              @toggle-status="updateReadStatus"
              @item-click="handleItemClick"
            />
          </transition>
        </BoxComponent>
      </template>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.notification-content__wrapper {
  width: min(100vw, 480px);
  border-radius: 16px;
  position: relative;
  height: calc(100vh - 80px - 32px);
  display: flex;
  flex-direction: column;
}

.notification-content__container {
  position: relative;
  height: 100%;
  background: var(--inverted);
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  border-radius: 16px;
}
.notification-content__buttons {
  position: sticky;
  top: -16px;
  z-index: 100;
  background: var(--inverted);
}
.notification-content__unavailable {
  padding: 16px;

  :deep(.el-alert) {
    font-size: 14px;
    padding: 16px 20px;
  }
}
.notification-content__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.slide-move {
  height: auto;
  overflow: hidden;
  position: absolute;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  height: auto;
}

.slide-enter-from {
  opacity: 0;
}

.slide-leave-to {
  opacity: 0;
  position: absolute;
  width: 100%;
}
:deep(.el-dropdown-menu) {
  border-radius: 16px;
}
</style>
